import { createContext, useContext, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { createTranslator } from "next-intl";
import { useForm } from "react-hook-form";
import { createUpload } from "../../api/upload";
import { createClaim } from "../../api/claim";
import { MainLayout } from "../../layouts/MainLayout";
import { Card } from "../../shared/Card";
import { useTenantTheme } from "../../themes/TenantThemeProvider";
import { ProgressBar } from "./components/ProgressBar";
import { HappyBox } from "../../svgs/HappyBox";
import { Alert, Box, Snackbar } from "@mui/material";
import { Loader } from "../../shared/Loader";
import { usePreFetchOrder, useScreenAnalytics } from "./hooks";
import { useAirbrake } from "../../context/AirbrakeContext";
import content from "../../content/claims.json";

const ClaimsContext = createContext();

export function buildEmptyClaimFormValues(orderData) {
  return {
    details: "",
    reason: "",
    resolution: "",
    images: [],
    items: orderData.line_items.map((lineItem) => ({
      issueType: "",
      itemId: lineItem.id,
      quantity: lineItem.quantity,
      reason: "",
      resolution: "",
      exchangeVariantId: "",
      exchangeVariantTitle: "",
      images: [],
    })),
  };
}

/*
ClaimsProvider is the top-level orchestrator for claims/returns flow;
it takes care of pre-fetching order if it exists, tracking analytics,
submitting a claim, and most importantly..holds the state for the
main claims form; we need to hold state in one top-level place in
order to go back and force and allow the user to edit after review;
the Claims component is in charge of the claims "routing" by renering
the step it needs; every component pulls actions it needs from the claims provider
*/

const ClaimsProvider = ({ children }) => {
  const airbrake = useAirbrake();
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [claimMessage, setClaimMessage] = useState({
    isActive: false,
    level: null,
    message: null,
  });
  const [, setSearchParams] = useSearchParams();
  const { siTheme, isAppLoading, isThemeLoading, locale } = useTenantTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [order, setOrder] = useState();
  const claimsForm = useForm();

  const t = createTranslator({
    messages: content[locale],
  });

  const onOrderLookup = (data) => {
    setOrder(data);
    setIsSelectAll(false);
    claimsForm.reset(buildEmptyClaimFormValues(data));
    setActiveStep(1);
  };

  const onOrderLookupError = (message) => {
    setClaimMessage({
      isActive: true,
      message,
      level: "error",
    });
  };

  const onErrorClose = () => {
    setClaimMessage({
      ...claimMessage,
      isActive: false,
    });
  };

  // fetch order if query param and track analytics
  usePreFetchOrder(onOrderLookup);
  useScreenAnalytics(activeStep);

  const setClaimFilingMode = (selectAll) => {
    if (!order) return;
    if (selectAll === isSelectAll) return;
    claimsForm.reset(buildEmptyClaimFormValues(order));
    setIsSelectAll(selectAll);
  };

  const resetClaimForm = () => {
    if (!order) return;
    claimsForm.reset(buildEmptyClaimFormValues(order));
  };

  const onClaimSubmit = async (values) => {
    setIsSubmitting(true);

    const images = values
      .filter((d) => (d.images || []).length)
      .map((d) => d.images)
      .flatMap((imgs) => imgs);
    let uploadedImages = [];

    if (images?.length) {
      try {
        uploadedImages = await Promise.all(
          images.map((image) => createUpload(image))
        );
      } catch (error) {
        const message = "Error uploading images";

        airbrake.notify({
          error: error,
          params: { message: message, values: values, context: "Image Upload", error: error },
        });

        setClaimMessage({
          isActive: true,
          message,
          level: "error",
        });

        setIsSubmitting(false);
        return;
      }
    }

    try {
      const sharedAdditionalInfo = values[0]?.details?.trim() ?? "";

      const payload = {
        // TODO once backfill is complete we can
        // remove these fields at the top level;
        // uses first item to support old model
        preferred_resolution: values[0].resolution,
        reason: values[0].reason,
        additional_info: sharedAdditionalInfo,

        from_page: "multi-tenant",
        email_address: order.email,
        shipInsureID: order.shipInsureID,
        items: values.map((item) => ({
          id: item.itemId,
          claim_quantity: item.quantity ?? item.lineItem?.quantity ?? 1,
          reason: item.reason,
          issue_type: item.issueType,
          preferred_resolution: item.resolution,
          ...(item.exchangeVariantId
            ? {
                exchange_variant_id: item.exchangeVariantId,
                exchange_variant_title: item.exchangeVariantTitle,
              }
            : {}),
        })),
        // images at the claim level, not item level
        images: uploadedImages.map((image) => ({
          key: image.data.file_name,
        })),
      };

      await createClaim(payload);

      // set for thank you page tracking link
      setSearchParams({
        shipInsureId: order.shipInsureID,
      });

      setActiveStep(3);
    } catch (error) {
      airbrake.notify({
        error: error,
        params: { message: "Error submitting claim", values: values, context: "Claim Submit" },
      });

      setClaimMessage({
        isActive: true,
        message: error.response?.data?.Message || error.message,
        level: "error",
      });
      setIsSubmitting(false);
    }
  };

  if (isAppLoading || isThemeLoading) {
    return <Loader />;
  }

  return (
    <ClaimsContext.Provider
      value={{
        locale,
        activeStep,
        setActiveStep,
        claimsForm,
        order,
        onOrderLookup,
        onOrderLookupError,
        onClaimSubmit,
        isSubmitting,
        setIsSubmitting,
        isSelectAll,
        setClaimFilingMode,
        resetClaimForm,
        setIsSelectAll,
      }}
    >
      <MainLayout helpText={t("helpText")}>
        <Card
          enableBrand={!siTheme.isPrimaryTheme}
          poweredByText={t("poweredByText")}
        >
          {activeStep === 3 && (
            <Box display="flex" justifyContent="center" mb="36px">
              <HappyBox />
            </Box>
          )}
          <ProgressBar width={getProgressBarWidth(activeStep)} />
          {children}
        </Card>
        <Snackbar
          open={claimMessage.isActive}
          autoHideDuration={3000}
          onClose={(_, reason) => {
            if (reason === "clickaway") return;
            onErrorClose();
          }}
        >
          <Alert
            onClose={onErrorClose}
            severity={claimMessage.level}
            sx={{ width: "100%" }}
            variant="filled"
          >
            {claimMessage.message}
          </Alert>
        </Snackbar>
      </MainLayout>
    </ClaimsContext.Provider>
  );
};

function getProgressBarWidth(activeStep) {
  switch (activeStep) {
    case 1: {
      return "33.33%";
    }
    case 2: {
      return "66.66%";
    }
    case 3: {
      return "100%";
    }
    case 0:
    default: {
      return "0%";
    }
  }
}

export const useClaims = () => useContext(ClaimsContext);
export { ClaimsProvider };
